import { SITE } from "@/seoData";
import DUMP_GUIDES from "./dumpGuides.json";

// Local-SEO blog / guide content — synced from backend DEFAULT_GUIDES (all 13 posts)
// so every guide renders instantly and never bounces even if the API is slow/down.
// The BlogPost page still fetches /api/guides/<slug> to pick up any CMS edits.
const ALL_POSTS = [
  {
    "slug": "junk-removal-cost-san-diego",
    "seoTitle": "How Much Does Junk Removal Cost in San Diego? (2026 Price Guide) | CleanOut247",
    "title": "How Much Does Junk Removal Cost in San Diego?",
    "description": "A clear 2026 breakdown of junk removal prices in San Diego — what you'll pay by load size, single-item rates, what affects the price, and how to avoid hidden fees.",
    "excerpt": "Wondering what a haul-away actually costs in San Diego? Here's an honest, upfront breakdown by load size and item — plus the hidden fees to watch for.",
    "date": "2026-05-12",
    "readTime": "6 min read",
    "category": "Pricing",
    "hero": "https://images.unsplash.com/photo-1616551569669-b60598758c4f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      {
        "type": "p",
        "text": "If you've searched \"junk removal cost San Diego,\" you've probably noticed almost nobody lists real prices. At CleanOut247 we price by volume — how much space your stuff takes up in our 15-cubic-yard truck — so you always get a firm, all-in number before we lift a finger. Here's exactly what to expect in 2026."
      },
      {
        "type": "h2",
        "text": "San Diego junk removal prices by load size"
      },
      {
        "type": "p",
        "text": "Most full-service junk removal in San Diego is priced by how full the truck gets. Our transparent tiers:"
      },
      {
        "type": "ul",
        "items": [
          "1/8 load (~2 cu yds): $185 full-service, $130 curbside — our minimum pickup",
          "1/4 load (~4 cu yds): $310",
          "1/3 load (~5 cu yds): $400",
          "1/2 load (~7.5 cu yds): $510",
          "2/3 load (~10 cu yds): $640",
          "3/4 load (~11 cu yds): $760",
          "Full load (15 cu yds): $900"
        ]
      },
      {
        "type": "p",
        "text": "That price includes all labor, loading, drive time, fuel, cleanup, and legal disposal or recycling. No weight surprises."
      },
      {
        "type": "h2",
        "text": "Single-item pricing"
      },
      {
        "type": "p",
        "text": "Only getting rid of one or two things? Flat per-item rates are often cheaper than a load. A few examples: a mattress runs about $65, a couch around $95, a refrigerator about $95, and a hot tub around $200. Just remember our $130 minimum applies."
      },
      {
        "type": "h2",
        "text": "What actually affects the price?"
      },
      {
        "type": "ul",
        "items": [
          "Volume — the #1 factor. The more space it fills, the more it costs.",
          "Access — 2+ flights of stairs, long carries (75+ ft), or tight alley access add labor.",
          "Heavy materials — concrete, dirt, tile and brick are dense and hit landfill weight fees.",
          "Special items — hot tubs, pianos and sheds take extra time and equipment."
        ]
      },
      {
        "type": "h2",
        "text": "How to avoid hidden junk removal fees"
      },
      {
        "type": "p",
        "text": "The cheapest phone quote is often the most expensive once the crew arrives. Protect yourself: get the all-in price in writing before work starts, confirm the company is licensed and insured, and ask where your junk actually goes. Unusually low prices sometimes mean illegal dumping — which in California carries fines up to $10,000 that can trace back to you."
      },
      {
        "type": "cta",
        "text": "Get a free, upfront San Diego junk removal quote in minutes."
      }
    ],
    "faqs": [
      {
        "q": "What is the average cost of junk removal in San Diego?",
        "a": "Most jobs land between $130 for a small single-item pickup and $900 for a full 15-cubic-yard truckload. The exact price depends on how much space your items take up, plus access and any heavy materials."
      },
      {
        "q": "Is there a minimum charge for junk removal?",
        "a": "Yes — CleanOut247's minimum pickup is $130 — a curbside 1/8 load (about 2 cubic yards); full-service carry-out for the same load is $185. This covers our labor, truck, fuel and disposal costs for coming out."
      },
      {
        "q": "Do you give free estimates?",
        "a": "Absolutely. You get a firm, no-obligation price on-site before we start, and there are no hidden fees. You can also get a ballpark instantly using our online estimate tool."
      }
    ],
    "related": [
      {
        "to": "/pricing-and-disposal-guide",
        "label": "Full Pricing & Disposal Guide"
      },
      {
        "to": "/services/furniture-removal",
        "label": "Furniture Removal"
      },
      {
        "to": "/junk-removal/san-diego",
        "label": "Junk Removal in San Diego"
      }
    ]
  },
  {
    "slug": "how-to-dispose-of-a-mattress-san-diego",
    "seoTitle": "How to Dispose of a Mattress in San Diego (Free & Paid Options 2026) | CleanOut247",
    "title": "How to Dispose of a Mattress in San Diego",
    "description": "Every way to get rid of an old mattress in San Diego in 2026 — free recycling drop-off, bulky-item pickup rules, donation, and same-day haul-away when you want it gone today.",
    "excerpt": "Old mattress taking up space? Here are your free and paid options in San Diego — recycling, city bulky pickup, donation, and same-day removal.",
    "date": "2026-05-26",
    "readTime": "5 min read",
    "category": "How-To",
    "hero": "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      {
        "type": "p",
        "text": "Mattresses are one of the trickiest things to get rid of — they're bulky, banned from most regular trash pickups, and illegal to dump. The good news: San Diego has several legitimate options, from free recycling to same-day haul-away. Here's how to pick the right one."
      },
      {
        "type": "h2",
        "text": "Option 1: Free mattress recycling (California's Bye Bye Mattress program)"
      },
      {
        "type": "p",
        "text": "California runs a statewide mattress recycling program funded by a fee you already paid at purchase, so drop-off is free at participating locations. You'll need to load and transport it yourself, and it must be dry and free of bed bugs. Search \"Bye Bye Mattress\" for the nearest San Diego collection site."
      },
      {
        "type": "h2",
        "text": "Option 2: City of San Diego bulky item pickup"
      },
      {
        "type": "p",
        "text": "If you get city trash service, you may qualify for a limited number of free bulky-item collections per year. You'll typically need to schedule ahead and place the mattress at the curb on your assigned day. Availability and rules vary by neighborhood and hauler, so confirm before you drag it out."
      },
      {
        "type": "h2",
        "text": "Option 3: Donate it (if it's still in good shape)"
      },
      {
        "type": "p",
        "text": "Gently used, clean mattresses can sometimes be donated to local charities or shelters. Note that many won't accept mattresses for hygiene reasons, and most require it to be stain- and damage-free, so call ahead."
      },
      {
        "type": "h2",
        "text": "Option 4: Same-day mattress removal"
      },
      {
        "type": "p",
        "text": "If you don't have a truck, don't want to wait for a scheduled city pickup, or need it gone today, full-service removal is the fastest route. We carry it down the stairs, out of the bedroom — you don't lift a thing — and make sure it's recycled properly. Single mattress removal starts around $65 (our $130 minimum applies)."
      },
      {
        "type": "h2",
        "text": "What you can't do"
      },
      {
        "type": "ul",
        "items": [
          "Leave it in an alley, canyon or by a dumpster — that's illegal dumping (fines up to $10,000 in CA).",
          "Put it in your regular curbside bin — mattresses aren't accepted.",
          "Burn it or break it down for regular trash — the components must be recycled."
        ]
      },
      {
        "type": "cta",
        "text": "Want it gone today? Book same-day mattress removal in San Diego."
      }
    ],
    "faqs": [
      {
        "q": "Is mattress disposal free in San Diego?",
        "a": "It can be. California's mattress recycling program offers free drop-off at participating sites if you can transport it yourself. City bulky-item pickup may also be free but is limited and must be scheduled. Full-service removal is paid but requires zero effort."
      },
      {
        "q": "Where can I drop off a mattress for free in San Diego?",
        "a": "California's Bye Bye Mattress program provides free drop-off at participating San Diego recycling sites, as long as the mattress is dry, free of bed bugs, and you can transport it yourself."
      },
      {
        "q": "Can I put a mattress in my regular trash?",
        "a": "No. Mattresses are not accepted in curbside bins and can't be dumped. They must be recycled or collected through an approved bulky-item or hauling service."
      },
      {
        "q": "How much does mattress removal cost?",
        "a": "Single mattress removal starts around $65, though our $130 pickup minimum applies. If you have other items to clear at the same time, load-based pricing is usually the better deal."
      }
    ],
    "related": [
      {
        "to": "/pricing-and-disposal-guide",
        "label": "Pricing & Disposal Guide"
      },
      {
        "to": "/services/furniture-removal",
        "label": "Furniture Removal"
      },
      {
        "to": "/junk-removal/san-diego",
        "label": "Junk Removal in San Diego"
      }
    ]
  },
  {
    "slug": "how-to-get-rid-of-old-appliances-san-diego",
    "seoTitle": "How to Get Rid of Old Appliances in San Diego (Fridges, Washers & More) | CleanOut247",
    "title": "How to Get Rid of Old Appliances in San Diego",
    "description": "The right way to dispose of old appliances in San Diego — why fridges and AC units need special handling, free scrap-metal and recycling options, and same-day appliance removal.",
    "excerpt": "Old fridge, washer or AC unit to get rid of? Some appliances need special freon handling. Here are your legal, safe options in San Diego.",
    "date": "2026-06-09",
    "readTime": "5 min read",
    "category": "How-To",
    "hero": "https://images.unsplash.com/photo-1663068307522-1731d9c8fbc2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      {
        "type": "p",
        "text": "Appliances are heavy, awkward, and some are regulated by law. Refrigerators, freezers and air conditioners contain refrigerants (freon/CFCs) that must be professionally removed before disposal. Here's how to get rid of old appliances in San Diego the right way."
      },
      {
        "type": "h2",
        "text": "Appliances that need special handling"
      },
      {
        "type": "ul",
        "items": [
          "Refrigerators & freezers — contain refrigerant that must be certified-removed.",
          "Air conditioners & dehumidifiers — same freon/CFC rules apply.",
          "Water heaters, washers, dryers, stoves — heavy but recyclable as scrap metal.",
          "Microwaves & small electronics — treated as e-waste, banned from the landfill."
        ]
      },
      {
        "type": "h2",
        "text": "Option 1: Retailer haul-away on delivery"
      },
      {
        "type": "p",
        "text": "Buying a replacement? Many retailers will haul away your old unit when they deliver the new one, sometimes free, sometimes for a small fee. Ask at checkout — but this only works if you're timing a new purchase."
      },
      {
        "type": "h2",
        "text": "Option 2: Scrap metal & recycling"
      },
      {
        "type": "p",
        "text": "Metal appliances have scrap value, and some recyclers accept them free if you can haul them yourself. Refrigerant-containing units usually carry a small handling fee for certified freon recovery (around $25 for fridges and AC units)."
      },
      {
        "type": "h2",
        "text": "Option 3: Same-day appliance removal"
      },
      {
        "type": "p",
        "text": "Appliances are heavy and can damage floors and doorways if you move them wrong. Full-service removal means our crew disconnects (where safe), carries it out, and delivers it to certified recyclers — freon handled correctly. No truck, no heavy lifting, no dump run."
      },
      {
        "type": "h2",
        "text": "What not to do"
      },
      {
        "type": "p",
        "text": "Never leave an old fridge on the curb or in an alley, and never puncture a refrigerant line. Both are illegal and can carry serious fines. Electronics and freon-bearing appliances are banned from the landfill in California."
      },
      {
        "type": "cta",
        "text": "Get fast, eco-friendly appliance removal across San Diego County."
      }
    ],
    "faqs": [
      {
        "q": "How do I dispose of a refrigerator in San Diego?",
        "a": "Refrigerators contain refrigerant that must be professionally removed, so they can't just go in the trash. You can use a certified recycler (usually a small freon-handling fee) or book appliance removal and we'll handle the certified disposal for you."
      },
      {
        "q": "Is appliance disposal free in San Diego?",
        "a": "Scrap-metal recyclers may take some metal appliances free if you transport them yourself. Freon-containing units like fridges and AC typically have a small handling fee (around $25). Full-service removal is paid but includes all lifting and certified recycling."
      },
      {
        "q": "Can I throw away a microwave or small appliance?",
        "a": "Electronics like microwaves are considered e-waste and are banned from the landfill in California. They must go to a certified e-waste recycler, which we handle as part of our service."
      }
    ],
    "related": [
      {
        "to": "/services/appliance-removal",
        "label": "Appliance Removal"
      },
      {
        "to": "/pricing-and-disposal-guide",
        "label": "Pricing & Disposal Guide"
      },
      {
        "to": "/junk-removal/san-diego",
        "label": "Junk Removal in San Diego"
      }
    ]
  },
  {
    "slug": "miramar-landfill-guide",
    "seoTitle": "Miramar Landfill: Fees, Hours & Rules (2026) | CleanOut247",
    "title": "Miramar Landfill Guide: Fees, Hours & What They Accept",
    "description": "Miramar Landfill fees, hours, address and accepted items for San Diego residents — plus an easier option if you'd rather not haul it yourself.",
    "excerpt": "Planning a dump run? Here are the Miramar Landfill's 2026 fees, hours, location and rules — plus what you're not allowed to dump.",
    "date": "2026-06-20",
    "readTime": "6 min read",
    "category": "Local Guide",
    "hero": "https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      { "type": "p", "text": "Also known locally as the Mira Mesa dump, the City of San Diego's Miramar Landfill & Recycling Center is where most local junk ends up. Last updated: June 2026. Below is the quick version — address, hours and 2026 fees — followed by the full details and the items they won't take." },
      { "type": "h2", "text": "Miramar Landfill at a glance" },
      { "type": "ul", "items": ["Address: 5180 Convoy St, San Diego, CA 92111", "Hours: Monday–Saturday (closed Sundays & holidays); no hand-unloads after 4 p.m.", "Phone: 858-694-7000", "Typical Tier 2 general-refuse fee: about $141–$149 per load (2026)", "Uncovered-load surcharge: $50 — always tarp your load"] },
      { "type": "h2", "text": "Miramar Landfill hours & location" },
      { "type": "p", "text": "The Miramar Landfill sits at 5180 Convoy St in the Mira Mesa area of San Diego, just off Highway 15. It's generally open Monday through Saturday and closed on Sundays and major holidays, with no hand-unloading after 4 p.m. Call 858-694-7000 to confirm the current gate hours before you drive over." },
      { "type": "h2", "text": "Miramar Landfill fees (2026 Tier 2 flat rates)" },
      { "type": "p", "text": "\"Tier 2\" covers pickup/trailer combos, larger trailers, and medium/heavy trucks — the class most household loads fall under. These flat rates are effective July 2025–June 2026, shown as city waste / non-city waste:" },
      { "type": "ul", "items": ["General Refuse: $141 / $149", "Special Handling: $197 / $209", "C&D (construction) Debris: $337 / $359", "Food Material: $159", "Green Material: $106", "Clean Wood: $53"] },
      { "type": "p", "text": "An uncovered load adds a $50 fee, so bring a tarp. Fees change annually and depend on vehicle type, waste type, weight and where the waste was generated — always verify current rates at sandiego.gov. For fees at other San Diego County disposal facilities, see our San Diego Dump & Landfill Fees guide (linked below)." },
      { "type": "h2", "text": "What Miramar accepts — and what they don't" },
      { "type": "p", "text": "Miramar takes most general household junk, furniture, yard waste, clean wood and construction debris. A few things are banned and need a different drop-off:" },
      { "type": "ul", "items": ["Electronics / e-waste (TVs, monitors, computers) — banned; go to a certified e-waste recycler (free in CA).", "Hazardous waste — paint, batteries, chemicals, fluorescent tubes; use the City's HHW facility by appointment.", "Refrigerators & AC units — refrigerant must be certified-removed first."] },
      { "type": "h2", "text": "Tips for a smoother dump run" },
      { "type": "ul", "items": ["Tarp and secure your load — an uncovered load is a $50 penalty.", "Go early on a weekday to skip the long weekend lines.", "Separate clean wood and green waste — they're charged at lower rates.", "Bring proof of city residency if you're claiming the city-waste rate.", "Have cash or a card ready at the scale house."] },
      { "type": "h2", "text": "Don't want to make the trip?" },
      { "type": "p", "text": "Between the tipping fee, fuel, tarping, waiting in line and the risk of overloading your vehicle, a DIY dump run adds up fast — and you still do all the loading. When you hire CleanOut247, the tipping fees, sorting, recycling and e-waste drop-offs are bundled into one flat San Diego junk removal price, and we do every bit of the lifting." },
      { "type": "cta", "text": "Skip the landfill line — get an all-in junk removal price today." }
    ],
    "faqs": [
      { "q": "How much does it cost to dump at the Miramar Landfill?", "a": "For Tier 2 vehicles in 2026, flat rates run from about $53 for clean wood and $106 for green material up to $141-$149 for general refuse and $337-$359 for construction debris. An uncovered load adds $50. Verify current rates at sandiego.gov." },
      { "q": "What are the Miramar Landfill hours?", "a": "It's generally open Monday through Saturday and closed on Sundays and holidays, with no hand-unloads after 4 p.m. All loads must be covered. Call 858-694-7000 to confirm current hours." },
      { "q": "Where is the Miramar Landfill (Mira Mesa dump)?", "a": "It's at 5180 Convoy St, San Diego, CA 92111, in the Mira Mesa area just off Highway 15. Call 858-694-7000 to confirm hours before heading over." },
      { "q": "Can I dump a TV or refrigerator at Miramar?", "a": "No. Electronics are banned from the landfill and must go to a certified e-waste recycler, and refrigerators need certified refrigerant removal first. We handle both correctly as part of our service." }
    ],
    "related": [
      {
        "to": "/pricing-and-disposal-guide",
        "label": "Pricing & Disposal Guide"
      },
      {
        "to": "/services/construction-debris-removal",
        "label": "Construction Debris Removal"
      },
      {
        "to": "/junk-removal/san-diego",
        "label": "Junk Removal in San Diego"
      }
    ]
  },
  {
    "slug": "what-can-you-put-in-a-junk-removal-truck",
    "seoTitle": "What Can You Put in a Junk Removal Truck? (San Diego Guide) | CleanOut247",
    "title": "What Can You Put in a Junk Removal Truck?",
    "description": "What junk removal takes and what it can't — furniture, appliances, e-waste, yard debris and construction material in San Diego, plus the hazardous items that need special disposal.",
    "excerpt": "Not sure if we'll take it? Here's what goes in a junk removal truck in San Diego — and the few things that can't.",
    "date": "2026-06-24",
    "readTime": "5 min read",
    "category": "How-To",
    "hero": "https://images.unsplash.com/photo-1614359835514-92f8ba196357?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      {
        "type": "p",
        "text": "A full-service junk removal truck can take almost anything non-hazardous — if you can point at it, we can usually haul it. Here's a clear list of what goes in the truck in San Diego, and the short list of items that need special handling instead."
      },
      {
        "type": "h2",
        "text": "What we take"
      },
      {
        "type": "ul",
        "items": [
          "Furniture — couches, mattresses, tables, dressers, office furniture",
          "Appliances — refrigerators, washers, dryers, stoves, water heaters",
          "Electronics & e-waste — TVs, computers, printers, monitors",
          "Yard & garage — fencing, wood, sheds, tools, boxes, bikes",
          "Construction debris — drywall, flooring, tile, cabinets, lumber",
          "Whole-home volume — estate, garage, and eviction cleanouts"
        ]
      },
      {
        "type": "h2",
        "text": "What we can't take (needs special disposal)"
      },
      {
        "type": "ul",
        "items": [
          "Hazardous waste — paint, motor oil, chemicals, pesticides",
          "Batteries, propane tanks and other pressurized/flammable items",
          "Asbestos and medical/biohazard waste"
        ]
      },
      {
        "type": "p",
        "text": "Refrigerators and AC units are fine — we handle the required freon recovery for you. If you're unsure about an item, send us a photo and we'll confirm before we arrive."
      },
      {
        "type": "cta",
        "text": "Have a pile that needs to go? Get a fast, free quote in San Diego."
      }
    ],
    "faqs": [
      {
        "q": "Will junk removal take a refrigerator or appliances?",
        "a": "Yes. We take refrigerators, freezers, washers, dryers, stoves and water heaters, and we handle the certified refrigerant (freon) removal that fridges and AC units require."
      },
      {
        "q": "What can't go in a junk removal truck?",
        "a": "Hazardous materials like paint, chemicals, motor oil, pesticides, batteries, propane tanks and asbestos can't be hauled with regular junk — they require special disposal. Everything non-hazardous is fair game."
      },
      {
        "q": "Do you take construction debris?",
        "a": "Yes — drywall, flooring, tile, lumber, cabinets and remodel debris are all accepted. Heavy, dense loads may affect pricing since they hit landfill weight fees."
      }
    ],
    "related": [
      {
        "to": "/pricing-and-disposal-guide",
        "label": "Pricing & Disposal Guide"
      },
      {
        "to": "/services/furniture-removal",
        "label": "Furniture Removal"
      },
      {
        "to": "/junk-removal/san-diego",
        "label": "Junk Removal in San Diego"
      }
    ]
  },
  {
    "slug": "furniture-removal-cost-san-diego",
    "seoTitle": "How Much Does Furniture Removal Cost in San Diego? (2026) | CleanOut247",
    "title": "How Much Does Furniture Removal Cost in San Diego?",
    "description": "2026 furniture removal prices in San Diego — per-item rates for couches, mattresses, dressers and more, plus how volume-based pricing works and what's included.",
    "excerpt": "What does it cost to haul away a couch, dresser or whole room of furniture in San Diego? Here are real 2026 prices.",
    "date": "2026-06-28",
    "readTime": "5 min read",
    "category": "Pricing",
    "hero": "https://images.unsplash.com/photo-1573866926487-a1865558a9cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      {
        "type": "p",
        "text": "Furniture removal in San Diego is priced either per item or by volume, whichever is cheaper for you. Here's what to expect in 2026, all-in with labor and disposal."
      },
      {
        "type": "h2",
        "text": "Per-item furniture prices"
      },
      {
        "type": "ul",
        "items": [
          "Couch or sofa: ~$95 (sectional ~$145, loveseat ~$85)",
          "Mattress: ~$65 (with box spring ~$85)",
          "Dresser or desk: ~$55",
          "Table: ~$45; table & chairs ~$75",
          "Recliner: ~$55; office/arm chair: ~$25"
        ]
      },
      {
        "type": "p",
        "text": "Our minimum pickup is $130 for a curbside 1/8 load (about 2 cubic yards; $185 full-service), so if you only have one small piece, combining a few items is the better value."
      },
      {
        "type": "h2",
        "text": "Whole-room or multi-item volume pricing"
      },
      {
        "type": "p",
        "text": "Clearing several pieces? Volume pricing is usually cheaper: a 1/4 truck runs $310, a 1/2 truck $510, and a full 15-yard load $900 — everything loaded, hauled and disposed."
      },
      {
        "type": "h2",
        "text": "What's included"
      },
      {
        "type": "p",
        "text": "Every price includes the heavy lifting, stairs and carry-out, drive time, fuel, and donation or recycling where possible. You never lift a thing and there are no hidden fees."
      },
      {
        "type": "cta",
        "text": "Get an upfront furniture removal quote in San Diego."
      }
    ],
    "faqs": [
      {
        "q": "How much does it cost to remove a couch in San Diego?",
        "a": "Couch removal is about $95 (sectionals ~$145), though our $130 minimum pickup applies. If you have other furniture to clear, volume pricing is often the better deal."
      },
      {
        "q": "Is it cheaper to pay per item or by the load?",
        "a": "For one or two pieces, per-item pricing is simplest. For a full room or several items, volume pricing (from $130 up to $900 for a full truck) is usually cheaper."
      },
      {
        "q": "Do you carry the furniture out for me?",
        "a": "Yes. We do all the lifting and carry it down stairs and out of the home — the price includes labor, hauling and disposal."
      }
    ],
    "related": [
      {
        "to": "/services/furniture-removal",
        "label": "Furniture Removal"
      },
      {
        "to": "/pricing-and-disposal-guide",
        "label": "Pricing & Disposal Guide"
      },
      {
        "to": "/junk-removal/san-diego",
        "label": "Junk Removal in San Diego"
      }
    ]
  },
  {
    "slug": "how-to-get-rid-of-an-old-couch-san-diego",
    "seoTitle": "How to Get Rid of an Old Couch in San Diego (2026) | CleanOut247",
    "title": "How to Get Rid of an Old Couch in San Diego",
    "description": "The easiest ways to get rid of an old couch in San Diego — donation, city bulky pickup, and same-day sofa removal with no heavy lifting.",
    "excerpt": "Old sofa to get rid of? Here are your donation, pickup and same-day removal options in San Diego.",
    "date": "2026-06-30",
    "readTime": "4 min read",
    "category": "How-To",
    "hero": "https://images.unsplash.com/photo-1577462637488-d74f6206201e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      {
        "type": "p",
        "text": "A worn-out couch is heavy, awkward, and won't fit in a car — but you have a few good options in San Diego depending on its condition and how fast you want it gone."
      },
      {
        "type": "h2",
        "text": "If it's still in good shape: donate it"
      },
      {
        "type": "p",
        "text": "Clean, undamaged sofas can go to local charities, and some offer free pickup for larger items. Snap a photo and call ahead, as acceptance depends on condition."
      },
      {
        "type": "h2",
        "text": "If you have city service: bulky-item pickup"
      },
      {
        "type": "p",
        "text": "City of San Diego trash customers may qualify for a limited number of free bulky-item curbside collections a year, scheduled in advance."
      },
      {
        "type": "h2",
        "text": "If you want it gone today: same-day removal"
      },
      {
        "type": "p",
        "text": "We carry the couch out — down stairs, through tight doorways — and donate or recycle it. Sofa removal starts around $95 (our $130 minimum applies), and we can usually come the same day."
      },
      {
        "type": "cta",
        "text": "Book same-day couch removal in San Diego."
      }
    ],
    "faqs": [
      {
        "q": "How do I get rid of a couch in San Diego?",
        "a": "You can donate it if it's clean and undamaged, schedule a city bulky-item pickup if you have city trash service, or book same-day sofa removal and we'll carry it out for you."
      },
      {
        "q": "Will a charity pick up my old couch?",
        "a": "Some San Diego charities offer free pickup for donatable furniture in good condition. Call ahead — most won't take stained, torn or heavily worn sofas."
      },
      {
        "q": "How much does couch removal cost?",
        "a": "Sofa removal starts around $95, with a $130 minimum pickup. We do all the lifting and carry-out and handle donation or recycling."
      }
    ],
    "related": [
      {
        "to": "/services/furniture-removal",
        "label": "Furniture Removal"
      },
      {
        "to": "/blog/furniture-removal-cost-san-diego",
        "label": "Furniture Removal Cost"
      },
      {
        "to": "/junk-removal/san-diego",
        "label": "Junk Removal in San Diego"
      }
    ]
  },
  {
    "slug": "where-to-dispose-of-appliances-san-diego",
    "seoTitle": "Where to Dispose of Appliances in San Diego (2026) | CleanOut247",
    "title": "Where to Dispose of Appliances in San Diego",
    "description": "Where to dispose of old appliances in San Diego — scrap-metal recyclers, retailer haul-away, refrigerant rules for fridges and AC, and same-day appliance pickup.",
    "excerpt": "Old fridge, washer or AC unit? Here's where appliances go in San Diego — and the freon rules to know.",
    "date": "2026-07-02",
    "readTime": "4 min read",
    "category": "Local Guide",
    "hero": "https://images.unsplash.com/photo-1663068307522-1731d9c8fbc2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      {
        "type": "p",
        "text": "Appliances are heavy and some are regulated, so they can't just go to the curb. Here's where to take them in San Diego, and the special rule for anything with refrigerant."
      },
      {
        "type": "h2",
        "text": "Scrap-metal recyclers"
      },
      {
        "type": "p",
        "text": "Washers, dryers, stoves and water heaters are mostly metal and accepted at scrap yards — sometimes free if you haul them yourself."
      },
      {
        "type": "h2",
        "text": "Retailer haul-away"
      },
      {
        "type": "p",
        "text": "Buying a replacement? Many retailers remove your old unit on delivery, free or for a small fee."
      },
      {
        "type": "h2",
        "text": "Fridges & AC units: freon must be removed"
      },
      {
        "type": "p",
        "text": "Refrigerators, freezers and air conditioners contain refrigerant that must be professionally recovered before disposal (a small handling fee, around $25). They're banned from the regular landfill."
      },
      {
        "type": "h2",
        "text": "Same-day appliance pickup"
      },
      {
        "type": "p",
        "text": "Don't want to load 200 lbs of metal? We disconnect (where safe), carry it out, and deliver it to certified recyclers with freon handled correctly."
      },
      {
        "type": "cta",
        "text": "Get eco-friendly appliance pickup across San Diego County."
      }
    ],
    "faqs": [
      {
        "q": "Where can I dispose of a refrigerator in San Diego?",
        "a": "Fridges need certified refrigerant removal first, so they can't go to the curb or regular landfill. Use a certified recycler (small freon fee) or book appliance pickup and we handle the certified disposal."
      },
      {
        "q": "Can I recycle a washer or dryer for free?",
        "a": "Often yes — metal appliances like washers, dryers and stoves have scrap value and some recyclers accept them free if you transport them yourself."
      },
      {
        "q": "Do you remove old appliances?",
        "a": "Yes, same-day across San Diego County. We handle the lifting and certified recycling, including freon recovery for fridges and AC units."
      }
    ],
    "related": [
      {
        "to": "/services/appliance-removal",
        "label": "Appliance Removal"
      },
      {
        "to": "/blog/how-to-get-rid-of-old-appliances-san-diego",
        "label": "How to Get Rid of Old Appliances"
      },
      {
        "to": "/junk-removal/san-diego",
        "label": "Junk Removal in San Diego"
      }
    ]
  },
  {
    "slug": "how-to-get-rid-of-construction-debris-san-diego",
    "seoTitle": "How to Get Rid of Construction Debris in San Diego (2026) | CleanOut247",
    "title": "How to Get Rid of Construction Debris in San Diego",
    "description": "How to dispose of construction and remodel debris in San Diego — C&D recycling, Miramar Landfill fees, dumpster vs. haul-away, and same-day debris removal.",
    "excerpt": "Remodel or demo debris to clear? Here's how construction waste is disposed of in San Diego, and the cheapest way to do it.",
    "date": "2026-07-04",
    "readTime": "5 min read",
    "category": "How-To",
    "hero": "https://images.unsplash.com/photo-1603655534191-ea4d80072bf6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      {
        "type": "p",
        "text": "Construction and demolition (C&D) debris — drywall, tile, lumber, concrete, cabinets — is heavy and often has to be recycled by law in San Diego. Here are your options."
      },
      {
        "type": "h2",
        "text": "C&D recycling & the Miramar Landfill"
      },
      {
        "type": "p",
        "text": "Much C&D material must be diverted to recycling. If you self-haul to the Miramar Landfill, 2026 construction-debris rates run about $337-$359 per Tier 2 load, plus a $50 fee for uncovered loads."
      },
      {
        "type": "h2",
        "text": "Dumpster vs. full-service haul-away"
      },
      {
        "type": "p",
        "text": "A rented dumpster sits on your property for days and you load it yourself. Full-service haul-away means we bring the truck and crew, load the heavy material, and take it to the right facility — often faster and with no driveway permit."
      },
      {
        "type": "h2",
        "text": "Watch the weight"
      },
      {
        "type": "p",
        "text": "Concrete, dirt, tile and brick are dense and hit landfill weight fees, so very heavy loads are priced accordingly. We give you a firm price on-site before we start."
      },
      {
        "type": "cta",
        "text": "Get construction debris hauled away fast in San Diego."
      }
    ],
    "faqs": [
      {
        "q": "Where does construction debris go in San Diego?",
        "a": "Most C&D debris must be diverted to recycling facilities; the rest goes to the Miramar Landfill (about $337-$359 per Tier 2 load in 2026). We sort and deliver material to the correct facility for you."
      },
      {
        "q": "Is a dumpster or junk removal cheaper for remodel debris?",
        "a": "For a quick, all-labor-included cleanup, full-service haul-away is usually faster and needs no permit. Dumpsters can make sense for long, ongoing projects where you load over several days."
      },
      {
        "q": "Do you take concrete and heavy debris?",
        "a": "Yes. Concrete, tile and brick are accepted, but because they're dense and trigger landfill weight fees, very heavy loads are priced accordingly — always confirmed upfront."
      }
    ],
    "related": [
      {
        "to": "/services/construction-debris-removal",
        "label": "Construction Debris Removal"
      },
      {
        "to": "/blog/miramar-landfill-guide",
        "label": "Miramar Landfill Guide"
      },
      {
        "to": "/junk-removal/san-diego",
        "label": "Junk Removal in San Diego"
      }
    ]
  },
  {
    "slug": "garage-cleanout-cost-san-diego",
    "seoTitle": "How Much Does a Garage Cleanout Cost in San Diego? (2026) | CleanOut247",
    "title": "How Much Does a Garage Cleanout Cost?",
    "description": "2026 garage cleanout prices in San Diego — what a half, single or double-garage clear-out costs by volume, what's included, and how to save.",
    "excerpt": "Reclaiming your garage? Here's what a cleanout costs in San Diego and how the pricing works.",
    "date": "2026-07-06",
    "readTime": "4 min read",
    "category": "Pricing",
    "hero": "https://images.unsplash.com/photo-1635108198854-26645ffe6714?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      {
        "type": "p",
        "text": "Garage cleanouts are priced by how much space the junk fills in our truck, so the cost depends on how packed your garage is. Here's a realistic 2026 range for San Diego."
      },
      {
        "type": "h2",
        "text": "Typical garage cleanout prices"
      },
      {
        "type": "ul",
        "items": [
          "Lightly cluttered / partial garage: ~$310-$400 (1/4 to 1/3 truck)",
          "Packed single garage: ~$510-$640 (1/2 to 2/3 truck)",
          "Double garage or hoarder-level: ~$760-$900 (3/4 to full truck)"
        ]
      },
      {
        "type": "p",
        "text": "Every price is all-in: our crew sorts, lifts, loads, sweeps up, and hauls it away, with donation and recycling included."
      },
      {
        "type": "h2",
        "text": "How to keep the cost down"
      },
      {
        "type": "p",
        "text": "Set aside anything you're keeping before we arrive, and group items so we can load efficiently. You get a firm, no-obligation price on-site before any work starts."
      },
      {
        "type": "cta",
        "text": "Get a free garage cleanout quote in San Diego."
      }
    ],
    "faqs": [
      {
        "q": "How much does a garage cleanout cost in San Diego?",
        "a": "Most garage cleanouts run from about $310 for a lightly cluttered space to $900 for a fully packed double garage, priced by how much space the junk fills in the truck. Labor, cleanup and disposal are included."
      },
      {
        "q": "Do you clear out everything, including shelving?",
        "a": "Yes — we remove furniture, boxes, tools, old appliances, shelving and general clutter, and sweep up afterward. Just set aside anything you want to keep."
      },
      {
        "q": "How long does a garage cleanout take?",
        "a": "Most take one to three hours depending on volume and access, and we can usually do it the same day."
      }
    ],
    "related": [
      {
        "to": "/services/garage-cleanout",
        "label": "Garage Cleanouts"
      },
      {
        "to": "/pricing-and-disposal-guide",
        "label": "Pricing & Disposal Guide"
      },
      {
        "to": "/junk-removal/san-diego",
        "label": "Junk Removal in San Diego"
      }
    ]
  },
  {
    "slug": "what-happens-to-your-junk-after-pickup",
    "seoTitle": "What Happens to Your Junk After We Pick It Up? | CleanOut247 San Diego",
    "title": "What Happens to Your Junk After We Pick It Up?",
    "description": "What CleanOut247 does with your junk after pickup in San Diego — donation, recycling, e-waste and certified appliance handling, and responsible landfill diversion.",
    "excerpt": "Ever wonder where your junk actually goes? Here's how we donate, recycle and responsibly dispose of it in San Diego.",
    "date": "2026-07-08",
    "readTime": "4 min read",
    "category": "About",
    "hero": "https://images.unsplash.com/photo-1612145342813-849b0c3e7f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      {
        "type": "p",
        "text": "We don't just drive everything to the dump. CleanOut247 sorts every load to keep as much as possible out of the landfill — it's better for San Diego and often required by law."
      },
      {
        "type": "h2",
        "text": "1. Donate what's usable"
      },
      {
        "type": "p",
        "text": "Furniture, working appliances, household goods and building materials in good shape go to local charities and reuse centers so they get a second life."
      },
      {
        "type": "h2",
        "text": "2. Recycle what can be recycled"
      },
      {
        "type": "p",
        "text": "Metal, cardboard, wood, and construction material are separated and taken to recycling facilities rather than the landfill."
      },
      {
        "type": "h2",
        "text": "3. Handle e-waste & appliances correctly"
      },
      {
        "type": "p",
        "text": "Electronics are dropped at certified e-waste recyclers (they're banned from the landfill in California), and refrigerant from fridges and AC units is professionally recovered."
      },
      {
        "type": "h2",
        "text": "4. Dispose of the rest responsibly"
      },
      {
        "type": "p",
        "text": "Only what truly can't be reused or recycled goes to a permitted facility like the Miramar Landfill — never dumped illegally."
      },
      {
        "type": "cta",
        "text": "Clear your space the responsible way — get a free quote."
      }
    ],
    "faqs": [
      {
        "q": "Do you recycle or donate the junk you pick up?",
        "a": "Yes. We sort every load to donate usable furniture, appliances and goods to local charities, recycle metal, wood, cardboard and e-waste, and only send true waste to a permitted facility."
      },
      {
        "q": "What happens to electronics and old appliances?",
        "a": "Electronics go to certified e-waste recyclers since they're banned from the landfill in California, and refrigerant in fridges and AC units is professionally recovered before recycling the metal."
      },
      {
        "q": "Do you ever dump junk illegally?",
        "a": "Never. Everything goes to donation, recycling, or a permitted disposal facility. Illegal dumping is against the law and against everything we stand for."
      }
    ],
    "related": [
      {
        "to": "/pricing-and-disposal-guide",
        "label": "Pricing & Disposal Guide"
      },
      {
        "to": "/blog/what-items-can-be-donated-instead-of-thrown-away",
        "label": "What Can Be Donated"
      },
      {
        "to": "/junk-removal/san-diego",
        "label": "Junk Removal in San Diego"
      }
    ]
  },
  {
    "slug": "what-items-can-be-donated-instead-of-thrown-away",
    "seoTitle": "What Items Can Be Donated Instead of Thrown Away? | CleanOut247 San Diego",
    "title": "What Items Can Be Donated Instead of Thrown Away?",
    "description": "What household items can be donated instead of trashed in San Diego — furniture, appliances, electronics, clothing and building materials — plus what charities can't accept.",
    "excerpt": "Before you toss it, donate it. Here's what San Diego charities will take — and what they won't.",
    "date": "2026-07-10",
    "readTime": "4 min read",
    "category": "How-To",
    "hero": "https://images.unsplash.com/photo-1541919833074-a92ca380fa4b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      {
        "type": "p",
        "text": "A lot of what people throw away still has plenty of life left. Donating keeps usable items out of the landfill and helps San Diego families. Here's what's typically welcome."
      },
      {
        "type": "h2",
        "text": "Commonly accepted donations"
      },
      {
        "type": "ul",
        "items": [
          "Furniture in good condition — sofas, tables, dressers, chairs",
          "Working appliances — microwaves, small kitchen appliances",
          "Electronics that still function — TVs, computers, speakers",
          "Clothing, shoes, linens and bedding (clean)",
          "Kitchenware, dishes and small household goods",
          "Building materials — cabinets, doors, fixtures (reuse centers)"
        ]
      },
      {
        "type": "h2",
        "text": "What charities usually can't take"
      },
      {
        "type": "ul",
        "items": [
          "Stained, torn or heavily worn furniture",
          "Mattresses (most decline for hygiene reasons)",
          "Broken or non-working appliances and electronics",
          "Recalled items, hazardous materials, or anything unsafe"
        ]
      },
      {
        "type": "p",
        "text": "When we do a cleanout, we set aside donatable items and take them to local charities for you — so the good stuff gets reused automatically."
      },
      {
        "type": "cta",
        "text": "Doing a cleanout? We'll donate the good stuff for you — get a quote."
      }
    ],
    "faqs": [
      {
        "q": "What household items can be donated instead of thrown away?",
        "a": "Furniture in good shape, working appliances and electronics, clean clothing and linens, kitchenware, and reusable building materials are all commonly accepted by San Diego charities and reuse centers."
      },
      {
        "q": "Can I donate a mattress or broken appliance?",
        "a": "Usually not — most charities decline mattresses for hygiene reasons and won't take broken appliances or electronics. Those should be recycled instead, which we handle."
      },
      {
        "q": "Will you donate items from my cleanout for me?",
        "a": "Yes. During any cleanout we set aside usable items and deliver them to local charities, so donatable goods are reused rather than landfilled — at no extra step for you."
      }
    ],
    "related": [
      {
        "to": "/blog/what-happens-to-your-junk-after-pickup",
        "label": "What Happens to Your Junk"
      },
      {
        "to": "/services/estate-cleanout",
        "label": "Estate Cleanouts"
      },
      {
        "to": "/junk-removal/san-diego",
        "label": "Junk Removal in San Diego"
      }
    ]
  },
  {
    "slug": "how-to-dispose-of-hazardous-waste-san-diego",
    "seoTitle": "How to Dispose of Hazardous Waste in San Diego (2026 Guide) | CleanOut247",
    "title": "How to Dispose of Hazardous Waste in San Diego",
    "description": "How to safely dispose of household hazardous waste in San Diego — paint, batteries, chemicals, motor oil and more — including the free HHW facility, appointment rules and what junk removal can't take.",
    "excerpt": "Paint, batteries, chemicals or old motor oil to get rid of? Here's how to dispose of household hazardous waste safely and legally in San Diego.",
    "date": "2026-07-14", "readTime": "5 min read", "category": "How-To",
    "hero": "https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      { "type": "p", "text": "Household hazardous waste (HHW) — paint, batteries, cleaning chemicals, pesticides, motor oil, propane and fluorescent tubes — is illegal to put in the trash or pour down the drain in San Diego, and it's the one category a junk removal truck can't legally haul. Here's how to get rid of it safely." },
      { "type": "h2", "text": "What counts as household hazardous waste?" },
      { "type": "ul", "items": ["Paint, stains, solvents and thinners", "Batteries (car, rechargeable and lithium)", "Motor oil, antifreeze and other automotive fluids", "Pesticides, herbicides and pool chemicals", "Propane tanks and other pressurized/flammable containers", "Fluorescent tubes, CFL bulbs and mercury thermostats"] },
      { "type": "h2", "text": "Option 1: The City of San Diego HHW Transfer Facility (free, by appointment)" },
      { "type": "p", "text": "City of San Diego residents can drop off household hazardous waste for free at the HHW Transfer Facility at the Miramar Landfill — but it's by appointment only, so book online before you go. There are limits on how much you can bring per visit. If you live in another jurisdiction, check your city's own HHW program." },
      { "type": "h2", "text": "Option 2: Retailer and store take-back" },
      { "type": "p", "text": "Many hardware and auto-parts stores take back specific items: paint through PaintCare drop-off sites, rechargeable and lithium batteries at electronics and hardware retailers, and motor oil and filters at auto-parts stores. It's often the fastest option for a single item type." },
      { "type": "h2", "text": "What CleanOut247 can and can't take" },
      { "type": "p", "text": "By law we can't put hazardous waste in the truck with regular junk, so paint, chemicals, batteries and propane need to go through the HHW facility or a take-back program. But when we do a garage, estate or whole-house cleanout, we'll set those items aside, tell you exactly where they go, and haul everything else — furniture, appliances, e-waste and general junk — in one trip." },
      { "type": "cta", "text": "Doing a cleanout? We'll haul the junk and point you to the right HHW drop-off." }
    ],
    "faqs": [
      { "q": "Where can I dispose of hazardous waste in San Diego?", "a": "City of San Diego residents can drop off household hazardous waste for free at the HHW Transfer Facility at the Miramar Landfill, by appointment only. Retailers also take back paint (PaintCare), batteries and motor oil. Residents of other cities should check their own local HHW program." },
      { "q": "Can a junk removal company take paint or chemicals?", "a": "No. Hazardous waste like paint, chemicals, batteries, motor oil and propane can't legally be hauled with regular junk. We'll set them aside during a cleanout and tell you exactly where to take them, then haul everything else." },
      { "q": "Is hazardous waste disposal free in San Diego?", "a": "For City of San Diego residents, HHW drop-off at the Miramar facility is free with an appointment, within per-visit limits. Many take-back programs for paint, batteries and oil are also free." }
    ],
    "related": [
      { "to": "/blog/miramar-landfill-guide", "label": "Miramar Landfill Guide" },
      { "to": "/blog/how-to-recycle-electronics-san-diego", "label": "Electronics Recycling" },
      { "to": "/junk-removal/san-diego", "label": "Junk Removal in San Diego" }
    ]
  },
  {
    "slug": "how-to-recycle-electronics-san-diego",
    "seoTitle": "How to Recycle Electronics & E-Waste in San Diego (2026) | CleanOut247",
    "title": "How to Recycle Electronics (E-Waste) in San Diego",
    "description": "How to recycle electronics and e-waste in San Diego — TVs, computers, monitors and phones — why they're banned from the landfill, free recycling options, and same-day e-waste pickup.",
    "excerpt": "Old TV, computer or pile of cables to get rid of? Electronics are banned from the landfill in California. Here's how to recycle e-waste in San Diego.",
    "date": "2026-07-16", "readTime": "5 min read", "category": "How-To",
    "hero": "https://images.unsplash.com/photo-1550009158-9ebf69173e03?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      { "type": "p", "text": "In California, electronic waste (e-waste) is banned from the regular landfill — you can't toss a TV, monitor or computer in the trash. The good news is that recycling electronics in San Diego is usually free, and for a pile of gear you don't want to haul yourself, same-day pickup is easy." },
      { "type": "h2", "text": "What counts as e-waste?" },
      { "type": "ul", "items": ["TVs, computer monitors and laptops", "Desktop computers, servers and networking gear", "Phones, tablets, printers and scanners", "Cables, chargers, keyboards and small electronics", "Microwaves and some small appliances"] },
      { "type": "h2", "text": "Why electronics can't go in the trash" },
      { "type": "p", "text": "Screens and circuit boards contain lead, mercury and other materials that leach into the environment, which is why California law (the Electronic Waste Recycling Act) bans them from the landfill. A recycling fee you already paid when you bought the device funds free take-back." },
      { "type": "h2", "text": "Option 1: Free e-waste recycling drop-off" },
      { "type": "p", "text": "Certified e-waste recyclers and periodic community collection events across San Diego accept most electronics free of charge if you drop them off yourself. Retailers like Best Buy also take back many devices. Data-bearing gear should be wiped first, though reputable recyclers offer data destruction." },
      { "type": "h2", "text": "Option 2: Same-day e-waste pickup" },
      { "type": "p", "text": "If you've got an old big-screen TV, a stack of monitors, or an office full of retired computers, hauling it yourself is a pain. CleanOut247 picks it up, does all the lifting, and delivers everything to certified e-waste recyclers so it's handled to code — no dump run, no heavy lifting." },
      { "type": "cta", "text": "Have a pile of old electronics? Book same-day e-waste pickup in San Diego." }
    ],
    "faqs": [
      { "q": "Where can I recycle electronics in San Diego?", "a": "Certified e-waste recyclers, retailer take-back programs (like Best Buy), and community collection events across San Diego accept most electronics for free if you drop them off. We also offer same-day e-waste pickup that delivers everything to certified recyclers." },
      { "q": "Can I throw away a TV or computer in San Diego?", "a": "No. Electronics are banned from the landfill in California, so TVs, monitors and computers can't go in the trash. They must go to a certified e-waste recycler." },
      { "q": "Is electronics recycling free in San Diego?", "a": "Usually yes — a recycling fee paid at purchase funds free e-waste drop-off at certified recyclers and retailers. Full-service pickup is paid but includes all the lifting and certified recycling." }
    ],
    "related": [
      { "to": "/services/e-waste-removal", "label": "E-Waste Removal" },
      { "to": "/blog/how-to-get-rid-of-old-appliances-san-diego", "label": "Appliance Disposal" },
      { "to": "/junk-removal/san-diego", "label": "Junk Removal in San Diego" }
    ]
  },
  {
    "slug": "san-diego-bulky-item-pickup-guide",
    "seoTitle": "San Diego Bulky Item Pickup & Bulk Trash Guide (2026) | CleanOut247",
    "title": "San Diego Bulky Item Pickup & Bulk Trash Guide",
    "description": "How bulky item and bulk trash pickup works in San Diego — free city collection rules and limits, what qualifies, and same-day haul-away when you can't wait or don't have city service.",
    "excerpt": "Got a couch, mattress or old appliance too big for the trash? Here's how San Diego bulky-item and bulk-trash pickup works — free options and faster ones.",
    "date": "2026-07-18", "readTime": "5 min read", "category": "Local Guide",
    "hero": "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    "body": [
      { "type": "p", "text": "\"Bulky items\" or \"bulk trash\" are the big things that don't fit in your curbside bins — couches, mattresses, appliances, furniture and large boxes. In San Diego you've got a few ways to get rid of them, from limited free city pickup to same-day haul-away. Here's how each works." },
      { "type": "h2", "text": "Option 1: City of San Diego bulky item collection" },
      { "type": "p", "text": "If the City of San Diego collects your trash, you likely qualify for a limited number of free bulky-item pickups per year. You schedule it in advance, then place the items at the curb on your assigned day. There are limits on how many items per pickup and rules about what qualifies, and service, limits and eligibility vary by neighborhood and hauler." },
      { "type": "h2", "text": "What usually qualifies as a bulky item" },
      { "type": "ul", "items": ["Furniture — couches, chairs, tables, dressers, mattresses", "Large appliances — refrigerators, washers, dryers, stoves (freon units may have extra rules)", "Water heaters, carpet rolls and large boxes"] },
      { "type": "h2", "text": "What bulk trash pickup won't take" },
      { "type": "p", "text": "City bulky collection generally won't take hazardous waste, construction and remodel debris, e-waste in some cases, or more than the per-pickup item limit. Those need a different drop-off or a hauling service." },
      { "type": "h2", "text": "Option 2: Same-day pickup — no waiting, no lifting" },
      { "type": "p", "text": "City pickups have to be scheduled and are limited, and you still have to drag everything to the curb yourself. If you rent, don't have city service, have more than the limit, or just want it gone today, CleanOut247 comes to you, carries it out from wherever it sits, and hauls it all in one trip — furniture, appliances, debris and more." },
      { "type": "cta", "text": "Skip the wait — book same-day bulky item pickup in San Diego." }
    ],
    "faqs": [
      { "q": "Does San Diego offer free bulky item pickup?", "a": "City of San Diego trash customers usually get a limited number of free bulky-item pickups per year, scheduled in advance with items placed at the curb. Limits, eligibility and rules vary by neighborhood and hauler, so confirm before you set anything out." },
      { "q": "What items count as bulk trash?", "a": "Large items that don't fit in your bins — couches, mattresses, furniture, and large appliances like refrigerators, washers and dryers. Hazardous waste, construction debris and items over the pickup limit generally don't qualify." },
      { "q": "What if I have more than the pickup limit or no city service?", "a": "That's where full-service removal helps. CleanOut247 offers same-day pickup with no item limit, carries everything out for you, and hauls it in one trip — ideal for renters, big clear-outs or when you can't wait for a scheduled city collection." }
    ],
    "related": [
      { "to": "/pricing-and-disposal-guide", "label": "Pricing & Disposal Guide" },
      { "to": "/blog/how-to-dispose-of-a-mattress-san-diego", "label": "Mattress Disposal" },
      { "to": "/junk-removal/san-diego", "label": "Junk Removal in San Diego" }
    ]
  }
];

const RETIRED_POSTS = ["junk-removal-cost-san-diego"];
const DUMP_SLUGS = DUMP_GUIDES.map((g) => g.slug);
export const POSTS = [...ALL_POSTS.filter((p) => !RETIRED_POSTS.includes(p.slug) && !DUMP_SLUGS.includes(p.slug)), ...DUMP_GUIDES];

export const getPost = (slug) => POSTS.find((p) => p.slug === slug);
export const BLOG_BASE = `${SITE}/blog`;
